import { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { X, Lock, AlertCircle, Check } from 'lucide-react';
import { Button } from './ui/button';
import api from '../lib/api';

/**
 * Stripe Payment Modal — uses Stripe.js v3 loaded from CDN (window.Stripe).
 *
 * Flow:
 *  1. Open → calls /api/payments/create-document-fee-intent → gets client_secret
 *  2. Mounts Stripe Elements PaymentElement
 *  3. User fills card → "Pay $500 now" → stripe.confirmPayment({ redirect: 'if_required' })
 *  4. On success → calls /api/payments/confirm-document-fee → backend verifies with Stripe
 *  5. onPaid() callback fires when document_access_paid=true on the user record
 */
export default function StripePaymentModal({ open, onClose, onPaid }) {
    const [stage, setStage] = useState('init'); // init | ready | processing | success | error
    const [errorMsg, setErrorMsg] = useState('');
    const [publishableKey, setPublishableKey] = useState('');
    const [paymentIntentId, setPaymentIntentId] = useState('');
    const [feeUsd, setFeeUsd] = useState(null); // server-confirmed amount
    const stripeRef = useRef(null);
    const elementsRef = useRef(null);
    const paymentElementRef = useRef(null);
    const mountedRef = useRef(false);

    useEffect(() => {
        if (!open) {
            mountedRef.current = false;
            return;
        }
        let cancelled = false;

        (async () => {
            setStage('init');
            setErrorMsg('');

            try {
                // 1. Get publishable key
                let pk = process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY || '';
                if (!pk) {
                    try {
                        const { data } = await api.get('/payments/config');
                        pk = data.publishable_key || '';
                    } catch { /* ignore */ }
                }
                if (cancelled) return;
                if (!pk) {
                    setStage('error');
                    setErrorMsg('Payments are not configured. Contact support.');
                    return;
                }
                setPublishableKey(pk);

                // 2. Wait for window.Stripe (loaded from CDN)
                if (!window.Stripe) {
                    setStage('error');
                    setErrorMsg('Stripe.js failed to load. Please refresh and try again.');
                    return;
                }

                // 3. Create PaymentIntent on backend
                let clientSecret;
                try {
                    const { data } = await api.post('/payments/create-document-fee-intent', {});
                    clientSecret = data.client_secret;
                    setPaymentIntentId(data.payment_intent_id);
                    if (typeof data.amount_usd === 'number') {
                        setFeeUsd(data.amount_usd);
                    } else if (typeof data.amount === 'number') {
                        setFeeUsd(data.amount / 100);
                    }
                } catch (err) {
                    if (cancelled) return;
                    setStage('error');
                    setErrorMsg(err.response?.data?.detail || 'Could not start payment. Please try again.');
                    return;
                }
                if (cancelled) return;

                // 4. Init Stripe + Elements
                const stripe = window.Stripe(pk);
                stripeRef.current = stripe;
                const elements = stripe.elements({
                    clientSecret,
                    appearance: { theme: 'stripe' },
                });
                elementsRef.current = elements;
                const paymentElement = elements.create('payment');
                paymentElementRef.current = paymentElement;

                // Mount when DOM ready. Target div is always rendered now (just hidden during init).
                const tryMount = (attempt = 0) => {
                    if (cancelled) return;
                    const target = document.getElementById('stripe-payment-element');
                    if (target && !mountedRef.current) {
                        try {
                            paymentElement.mount('#stripe-payment-element');
                            mountedRef.current = true;
                            setStage('ready');
                        } catch (mountErr) {
                            console.error('Stripe mount failed:', mountErr);
                            setStage('error');
                            setErrorMsg('Could not load payment form. Please refresh and try again.');
                        }
                    } else if (attempt < 40) {
                        setTimeout(() => tryMount(attempt + 1), 50);
                    } else if (!cancelled) {
                        setStage('error');
                        setErrorMsg('Payment form timed out. Please refresh and try again.');
                    }
                };
                tryMount();
            } catch (err) {
                console.error('StripePaymentModal init failed:', err);
                if (!cancelled) {
                    setStage('error');
                    setErrorMsg(err.message || 'Failed to initialize payment. Please try again.');
                }
            }
        })();

        return () => { cancelled = true; };
    }, [open]);

    const handlePay = async () => {
        if (!stripeRef.current || !elementsRef.current) return;
        setStage('processing');
        setErrorMsg('');
        try {
            const { error, paymentIntent } = await stripeRef.current.confirmPayment({
                elements: elementsRef.current,
                redirect: 'if_required',
                confirmParams: {
                    return_url: window.location.href,
                },
            });
            if (error) {
                setStage('ready');
                setErrorMsg(error.message || 'Payment failed. Please try again.');
                return;
            }
            const piId = paymentIntent?.id || paymentIntentId;
            // Verify with backend (server-side confirmation is the source of truth)
            try {
                const { data } = await api.post('/payments/confirm-document-fee', {
                    payment_intent_id: piId,
                });
                if (data.document_access_paid) {
                    setStage('success');
                    if (onPaid) onPaid();
                } else {
                    // Payment may still be processing
                    setStage('error');
                    setErrorMsg('Payment is still processing. Please refresh in a moment.');
                }
            } catch (err) {
                setStage('error');
                setErrorMsg(err.response?.data?.detail || 'Payment confirmed by Stripe but backend verification failed. Please contact support.');
            }
        } catch (err) {
            setStage('error');
            setErrorMsg('An unexpected error occurred. Please try again.');
        }
    };

    if (!open) return null;

    const modal = (
        <div
            className="fixed inset-0 z-[100] flex sm:items-center sm:justify-center bg-black/70 p-0 sm:p-4"
            onClick={stage === 'processing' ? undefined : onClose}
            data-testid="stripe-modal-overlay"
        >
            <div
                className="bg-white border-2 border-zinc-900 w-full sm:max-w-md max-h-[95vh] overflow-y-auto mt-auto sm:mt-0"
                onClick={e => e.stopPropagation()}
                data-testid="stripe-payment-modal"
            >
                {/* Header */}
                <div className="flex items-center justify-between border-b border-zinc-200 px-5 py-4">
                    <div className="flex items-center gap-2">
                        <Lock className="w-4 h-4 text-zinc-700" />
                        <h3 className="font-heading font-bold text-base">Document Access Fee</h3>
                    </div>
                    {stage !== 'processing' && (
                        <button onClick={onClose} className="text-zinc-400 hover:text-zinc-900" data-testid="stripe-modal-close" aria-label="Close">
                            <X className="w-5 h-5" />
                        </button>
                    )}
                </div>

                {/* Body */}
                <div className="px-5 py-4 space-y-4">
                    <div className="border border-zinc-200 p-3 bg-zinc-50">
                        <p className="text-xs text-zinc-400 uppercase tracking-wide mb-1">Total</p>
                        <p className="font-heading text-2xl font-black" data-testid="stripe-amount-display">
                            {feeUsd !== null ? `$${Number(feeUsd).toFixed(2)} USD` : '—'}
                        </p>
                        <p className="text-xs text-zinc-500 mt-1">One-time fee to unlock your visa and flight ticket.</p>
                    </div>

                    {stage === 'init' && (
                        <div className="flex items-center justify-center py-8" data-testid="stripe-loading">
                            <div className="w-6 h-6 border-2 border-zinc-900 border-t-transparent animate-spin" />
                            <span className="ml-3 text-sm text-zinc-500">Preparing secure payment...</span>
                        </div>
                    )}

                    {(stage === 'init' || stage === 'ready' || stage === 'processing') && stage !== 'success' && (
                        <div
                            data-testid="stripe-element-wrapper"
                            className={stage === 'init' ? 'opacity-0 h-0 overflow-hidden' : ''}
                        >
                            <div id="stripe-payment-element" className="min-h-[200px]" data-testid="stripe-payment-element" />
                        </div>
                    )}

                    {stage === 'success' && (
                        <div className="border border-green-300 bg-green-50 p-4 text-center" data-testid="stripe-success">
                            <Check className="w-8 h-8 text-green-700 mx-auto mb-2" />
                            <p className="font-heading font-bold text-green-800">Payment successful</p>
                            <p className="text-sm text-green-700 mt-1">Your documents are now available.</p>
                        </div>
                    )}

                    {errorMsg && (
                        <div className="border border-red-300 bg-red-50 p-3 flex items-start gap-2" data-testid="stripe-error">
                            <AlertCircle className="w-4 h-4 text-red-700 flex-shrink-0 mt-0.5" />
                            <p className="text-sm text-red-700">{errorMsg}</p>
                        </div>
                    )}
                </div>

                {/* Footer */}
                {stage !== 'success' && (
                    <div className="flex flex-col-reverse sm:flex-row gap-2 border-t border-zinc-200 px-5 py-4">
                        <Button
                            variant="outline"
                            className="flex-1 rounded-none h-12 font-semibold"
                            onClick={onClose}
                            disabled={stage === 'processing'}
                            data-testid="stripe-cancel-btn"
                        >
                            Cancel
                        </Button>
                        <Button
                            className="flex-1 rounded-none h-12 bg-zinc-900 text-white font-semibold"
                            onClick={handlePay}
                            disabled={stage !== 'ready'}
                            data-testid="stripe-pay-btn"
                        >
                            {stage === 'processing' ? 'Processing...' : (feeUsd !== null ? `Pay $${Number(feeUsd).toFixed(2)} now` : 'Pay now')}
                        </Button>
                    </div>
                )}
                {stage === 'success' && (
                    <div className="border-t border-zinc-200 px-5 py-4">
                        <Button
                            className="w-full rounded-none h-12 bg-zinc-900 text-white font-semibold"
                            onClick={onClose}
                            data-testid="stripe-success-close"
                        >
                            Continue to my documents
                        </Button>
                    </div>
                )}
                <p className="text-[10px] text-zinc-400 text-center pb-4 px-5">
                    Secured by Stripe. We never see or store your card details.
                </p>
            </div>
        </div>
    );

    return createPortal(modal, document.body);
}
